/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import './HistoryReport.scss';
import { useParams } from 'react-router-dom';
import { Button, Table, Form, FormControl, Modal, Row, Col } from 'react-bootstrap';
import { saveAs } from 'file-saver';
import jwt_decode from 'jwt-decode';
import { serverUrl } from '../../../serverUrl';
import { toast } from 'react-toastify';
import { X, Wallet, Landmark, Phone, Calendar, DollarSign, Home, Zap, Loader2, Trash2 } from 'lucide-react';

const normalizeInvoicePeriod = (value) => {
    if (!value) return "monthly";
    const normalized = value.toString().trim().toLowerCase().replace(/[_\s]/g, "-");
    if (["monthly", "month"].includes(normalized)) return "monthly";
    if (["quarterly", "quartely", "quarter"].includes(normalized)) return "quarterly";
    if (["semi-annual", "semi-annually", "semiannual", "semiannually", "semi-annualy", "semiannualy"].includes(normalized)) {
        return "semi-annual";
    }
    if (["annually", "annual", "yearly", "year"].includes(normalized)) return "annually";
    return normalized;
};

function InvoiceReport({ tenantDetails, unitDetails, tenantUserId, occupancyId = null, houseOccupancies = [] }) {
    const { id } = useParams();
    const [houseSelected, setHouseSelected] = useState([]);
    const [houseSelectedReport, setHouseSelectedReport] = useState([]);
    const [houseSelectedReportPaid, setHouseSelectedReportPaid] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [tenantsInvoice, setTenantInvoice] = useState({})
    const [isSaving, setIsSaving] = useState(false);
    const accessToken = localStorage.getItem('authTokens');
    // get the access token
    const parsedTokens = JSON.parse(accessToken);
    const access = parsedTokens.access;

    // headers access token
    const config = useMemo(() => ({
        headers: { Authorization: `Bearer ${access}` },
    }), [access]);
    // decoding the token so that i can get the user id
    const decodedToken = jwt_decode(accessToken);
    const userId = decodedToken.user_id;
    // filter based on the tenant name
    const [selectedTenant, setTenantSelected] = useState(null);

    // Filter houses array to get unique rent amounts
    const [showModalPayment, setShowModalPayment] = useState(false)
    const [selectedPaymentInvoice, setSelectedPaymentInvoice] = useState({})
    const [paymentTreatment, setPaymentTreatment] = useState('cash')
    const [withholdingMode, setWithholdingMode] = useState('preset')
    const [withholdingTaxAmount, setWithholdingTaxAmount] = useState('')
    const [showAddInvoiceModal, setShowAddInvoiceModal] = useState(false)
    const [isCreatingManualInvoice, setIsCreatingManualInvoice] = useState(false)
    const [deletingInvoiceId, setDeletingInvoiceId] = useState(null)
    const [showDeleteInvoiceModal, setShowDeleteInvoiceModal] = useState(false)
    const [invoicePendingDelete, setInvoicePendingDelete] = useState(null)
    const [showEditInvoiceModal, setShowEditInvoiceModal] = useState(false)
    const [invoicePendingEdit, setInvoicePendingEdit] = useState(null)
    const [editInvoiceAmount, setEditInvoiceAmount] = useState('')
    const [editInvoiceMonth, setEditInvoiceMonth] = useState('')
    const [editInvoiceYear, setEditInvoiceYear] = useState('')
    const [editInvoiceUser, setEditInvoiceUser] = useState('')
    const [editInvoicePeriod, setEditInvoicePeriod] = useState('')
    const [isEditingInvoice, setIsEditingInvoice] = useState(false)
    const [availableExpenses, setAvailableExpenses] = useState([])

    const monthNames = useMemo(() => ([
        "january", "february", "march", "april", "may", "june",
        "july", "august", "september", "october", "november", "december"
    ]), []);
    const currentMonthName = monthNames[new Date().getMonth()];
    const currentYear = new Date().getFullYear();
    const todayValue = useMemo(() => {
        const today = new Date();
        const offsetDate = new Date(today.getTime() - today.getTimezoneOffset() * 60000);
        return offsetDate.toISOString().split('T')[0];
    }, []);
    const manualInvoicePeriod = normalizeInvoicePeriod(
        unitDetails?.generate_invoice_schedule || unitDetails?.invoice_period || 'monthly'
    );
    const manualCycleMonths = ({ monthly: 1, quarterly: 3, 'semi-annual': 6, annually: 12 })[manualInvoicePeriod] || 1;
    const [manualInvoiceForm, setManualInvoiceForm] = useState({
        user_id: '',
        year: currentYear,
        months: [currentMonthName],
        include_rent: true,
        include_deposit: false,
        include_booking: false,
        expense_ids: [],
        consolidate_to_rent: false,
        generation_date: todayValue,
    });
    const tenantOptions = useMemo(() => {
        const source = Array.isArray(houseOccupancies) ? houseOccupancies : [];
        const uniqueByUser = new Map();
        source.forEach((occupancy) => {
            if (!occupancy?.user_id) return;
            const existing = uniqueByUser.get(occupancy.user_id);
            const isCurrent = occupancy.status === 'active' || occupancy.is_current;
            if (!existing || isCurrent) {
                uniqueByUser.set(occupancy.user_id, {
                    user_id: occupancy.user_id,
                    tenant_name: occupancy.tenant_name || `Tenant ${occupancy.user_id}`,
                    is_current: isCurrent,
                    date_in: occupancy.date_in,
                });
            }
        });
        return Array.from(uniqueByUser.values()).sort((a, b) => Number(b.is_current) - Number(a.is_current));
    }, [houseOccupancies]);

    const handleOpenModalPayment = (tenantInvoice) => {
        setShowModalPayment(true)
        setSelectedPaymentInvoice(tenantInvoice)
        setPaymentTreatment('cash')
        setWithholdingMode('preset')
        setWithholdingTaxAmount('')
        setFormDataPayment((previous) => ({
            ...previous,
            amount: String(tenantInvoice?.effective_balance ?? tenantInvoice?.effective_amount ?? tenantInvoice?.amount ?? ''),
        }))
    }
    const handleCloseModalPayment = () => {
        setShowModalPayment(false)
        setPaymentTreatment('cash')
        setWithholdingMode('preset')
        setWithholdingTaxAmount('')
    }
    const handlePaymentTreatmentChange = (treatment, checked) => {
        const nextTreatment = checked ? treatment : 'cash';
        setPaymentTreatment(nextTreatment);

        if (!checked) return;
        const invoiceAmount = Number(selectedPaymentInvoice?.effective_balance ?? selectedPaymentInvoice?.effective_amount ?? selectedPaymentInvoice?.amount ?? 0);
        const rentAmount = Number(
            selectedPaymentInvoice?.billing_breakdown?.rent
            || unitDetails?.rent
            || 0
        );
        const suggestedAmount = treatment === 'withholding_tax'
            ? Math.max(invoiceAmount - (rentAmount * 0.16), 0)
            : invoiceAmount;
        if (treatment === 'withholding_tax') {
            setWithholdingMode('preset');
            setWithholdingTaxAmount((rentAmount * 0.16).toFixed(2));
        }
        setFormDataPayment((previous) => ({
            ...previous,
            amount: suggestedAmount.toFixed(2),
        }));
    };
    const [landlordApartment, setLandlordApartment] = useState([])

    const buildInvoiceParams = useCallback((targetUserId, targetOccupancyId) => {
        const params = new URLSearchParams();
        params.append('house_id', id);
        if (targetUserId) {
            params.append('user_id', targetUserId);
        }
        if (targetOccupancyId) {
            params.append('occupancy_id', targetOccupancyId);
        }
        return params.toString();
    }, [id]);

    const extractBlobErrorMessage = useCallback(async (error, fallbackMessage) => {
        const blobData = error?.response?.data;
        if (blobData instanceof Blob) {
            try {
                const rawText = await blobData.text();
                if (rawText) {
                    try {
                        const parsed = JSON.parse(rawText);
                        if (parsed?.error) {
                            return parsed.error;
                        }
                    } catch (parseError) {
                        return rawText;
                    }
                }
            } catch (blobReadError) {
                console.log(blobReadError);
            }
        }

        return error?.response?.data?.error || fallbackMessage;
    }, []);

    const fetchHouseInvoices = useCallback((targetUserId, targetOccupancyId = occupancyId) => {
        if (!targetUserId && !targetOccupancyId) {
            setHouseSelectedReport([]);
            setTenantInvoice({});
            return Promise.resolve();
        }

        const params = buildInvoiceParams(targetUserId, targetOccupancyId);
        return serverUrl.get(`/landlord/tenant-invoice/?${params}`, config)
            .then((res) => {
                const normalizedInvoices = (res.data.results || []).map((invoice) => ({
                    ...invoice,
                    status: invoice.calculated_status || invoice.status,
                }));
                setHouseSelectedReport(normalizedInvoices);
                const names = {};
                const promises = normalizedInvoices.map((tenant) => {
                    return serverUrl
                        .get(`/users/users-details/${tenant.user}/`, config)
                        .then((userRes) => {
                            names[tenant.user] = userRes.data.full_name;
                        })
                        .catch((error) => {
                            console.log(error);
                        });
                });

                return Promise.all(promises).then(() => {
                    setTenantInvoice(names);
                });
            })
            .catch((error) => {
                console.log(error);
            });
    }, [buildInvoiceParams, config, occupancyId]);

    // get the house selected by user
    useEffect(() => {
        setIsLoading(true);

        // get the landlord details that are linked to the user
        serverUrl.get(`/landlord/landlord/?user_id=${userId}`, config)
            .then((res) => {
                // get the landlord Id
                const landlordIds = res.data.results.map((landlord) => landlord.id);
                // using the landlord id get the apartments linked to the landlord
                const landlordApartmentPromises = landlordIds.map((landlordId) => {
                    return serverUrl.get(`/landlord/landlord-apartment_name/?landlord_id=${landlordId}`, config)
                        .then((res) => res.data.results)
                        .catch((error) => {
                            console.log(error);
                            return [];
                        });
                });

                Promise.all(landlordApartmentPromises)
                    .then((results) => {
                        // i am merging the sub-arrays into a new array
                        const apartments = results.flat();
                        setLandlordApartment(apartments);
                        // getting the houses in the apartment


                    })
                    .catch((error) => {
                        console.log(error);
                        setIsLoading(false)

                    });
            })
            .catch((error) => {
                console.log(error);
            });

        // get house selected by user
        serverUrl.get(`/landlord/house/${id}/`, config)
            .then((res) => {
                setHouseSelected(res.data);
                setIsLoading(false);
            })
            .catch((error) => {
                console.log(error);
                setIsLoading(false);
            });
    }, [id])


    useEffect(() => {
        if (tenantUserId === undefined && !occupancyId) {
            return;
        }

        if (!tenantUserId && !occupancyId) {
            setHouseSelectedReport([]);
            setTenantInvoice({});
            return;
        }

        fetchHouseInvoices(tenantUserId, occupancyId);
    }, [tenantUserId, occupancyId, fetchHouseInvoices]);




    // declare the selected house
    const selectedUnit = houseSelected;
    const idHouse = parseInt(id);

    // download all invoices 
    function handleDownload() {
        try {
            if (!tenantUserId && !occupancyId) {
                toast.error('Select a tenant to download invoice reports.');
                return;
            }
            setIsLoading(true);
            const params = buildInvoiceParams(tenantUserId, occupancyId);
            serverUrl
                .get(`/tenant/generate_pdf_invoice/?${params}&download=true`, { ...config, responseType: 'blob' })
                .then((res) => {
                    // create a download url for the pdf and then automatically download the pdf
                    const downloadUrl = window.URL.createObjectURL(new Blob([res.data]));
                    const link = document.createElement('a');
                    link.href = downloadUrl;
                    link.setAttribute('download', 'house_invoice_report.pdf');
                    document.body.appendChild(link);
                    link.click();
                    // when pdf downloaded succesffully
                    toast.success('Successfully Downloaded Your House Invoice Report');
                    setIsLoading(false);
                })
                .catch((error) => {
                    console.log(error);
                });
        } catch (error) { }
    }

    async function handleViewRentalStatement() {
        let previewWindow = null;
        try {
            if (!tenantUserId && !occupancyId) {
                toast.error('Select a tenant to view the rental statement.');
                return;
            }

            previewWindow = window.open('', '_blank');
            setIsLoading(true);
            const params = buildInvoiceParams(tenantUserId, occupancyId);
            const res = await serverUrl.get(
                `/tenant/generate_rental_statement/?${params}&download=true`,
                { ...config, responseType: 'blob' }
            );
            const pdfUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));

            if (previewWindow && !previewWindow.closed) {
                previewWindow.location.href = pdfUrl;
            } else {
                const link = document.createElement('a');
                link.href = pdfUrl;
                link.target = '_blank';
                link.rel = 'noopener noreferrer';
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
            }

            toast.success('Rental statement opened successfully.');
            setIsLoading(false);
        } catch (error) {
            console.log(error);
            if (previewWindow && !previewWindow.closed) {
                previewWindow.close();
            }
            const errorMessage = await extractBlobErrorMessage(error, 'Failed to generate the rental statement.');
            toast.error(errorMessage);
            setIsLoading(false);
        }
    }

    // download one invoice
    async function handleDownloadInvoice(invoiceId) {
        setIsLoading(true);
        try {
            const res = await serverUrl.get(
                `/tenant/generate_pdf_invoice/?invoice_id=${invoiceId}&download=true`,
                { ...config, responseType: 'blob', timeout: 45000 }
            );
            const downloadUrl = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.setAttribute('download', 'invoice_report.pdf');
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            toast.success('Successfully Downloaded Your Invoice Report');
        } catch (error) {
            const message = await extractBlobErrorMessage(error, 'Failed to download the invoice.');
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    }

    // get invoice for one invoice display
    const handleViewPDFInvoice = async (invoiceId) => {
        const previewWindow = window.open('', '_blank');
        setIsLoading(true);
        try {
            const res = await serverUrl.get(
                `/tenant/generate_pdf_invoice/?invoice_id=${invoiceId}&download=true`,
                { ...config, responseType: 'blob', timeout: 45000 }
            );
            const pdfUrl = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            if (previewWindow && !previewWindow.closed) {
                previewWindow.location.href = pdfUrl;
            }
        } catch (error) {
            if (previewWindow && !previewWindow.closed) previewWindow.close();
            const message = await extractBlobErrorMessage(error, 'Failed to open the invoice.');
            toast.error(message);
        } finally {
            setIsLoading(false);
        }
    }

    const resetManualInvoiceForm = useCallback(() => {
        const fallbackTenant = tenantOptions.find((tenant) => tenant.is_current)?.user_id || tenantOptions[0]?.user_id || '';
        setManualInvoiceForm({
            user_id: tenantUserId || fallbackTenant || '',
            year: currentYear,
            months: [currentMonthName],
            include_rent: true,
            include_deposit: false,
            include_booking: false,
            expense_ids: [],
            consolidate_to_rent: false,
            generation_date: todayValue,
        });
    }, [tenantUserId, tenantOptions, currentYear, currentMonthName, todayValue]);

    useEffect(() => {
        resetManualInvoiceForm();
    }, [resetManualInvoiceForm]);

    const fetchApartmentExpenses = useCallback(() => {
        const apartmentId = unitDetails?.apartment || houseSelected?.apartment;
        if (!apartmentId) {
            setAvailableExpenses([]);
            return Promise.resolve();
        }
        return serverUrl.get(`/landlord/expense/?apartment_id=${apartmentId}`, config)
            .then((res) => {
                const expenses = (res.data?.results || []).filter((expense) => expense?.is_visible !== false);
                setAvailableExpenses(expenses);
            })
            .catch((error) => {
                console.log(error);
                setAvailableExpenses([]);
            });
    }, [unitDetails, houseSelected, config]);

    const handleOpenAddInvoiceModal = () => {
        resetManualInvoiceForm();
        fetchApartmentExpenses();
        setShowAddInvoiceModal(true);
    };

    const handleCloseAddInvoiceModal = () => {
        setShowAddInvoiceModal(false);
    };

    const handleManualMonthToggle = (month) => {
        setManualInvoiceForm((prev) => {
            const exists = prev.months.includes(month);
            if (exists) {
                return { ...prev, months: prev.months.filter((item) => item !== month) };
            }
            if (prev.months.length >= 3) {
                toast.error('You can only pick up to 3 months.');
                return prev;
            }
            if (prev.include_rent && manualCycleMonths > 1) {
                return { ...prev, months: [month] };
            }
            return { ...prev, months: [...prev.months, month] };
        });
    };

    const handleManualExpenseToggle = (expenseId) => {
        setManualInvoiceForm((prev) => {
            const exists = prev.expense_ids.includes(expenseId);
            return {
                ...prev,
                expense_ids: exists
                    ? prev.expense_ids.filter((item) => item !== expenseId)
                    : [...prev.expense_ids, expenseId],
            };
        });
    };

    const handleCreateManualInvoices = (event) => {
        event.preventDefault();

        if (!manualInvoiceForm.user_id) {
            toast.error('Select a tenant for this invoice.');
            return;
        }
        if (manualInvoiceForm.months.length === 0) {
            toast.error('Select at least one month.');
            return;
        }
        if (
            !manualInvoiceForm.include_rent
            && !manualInvoiceForm.include_deposit
            && !manualInvoiceForm.include_booking
            && manualInvoiceForm.expense_ids.length === 0
        ) {
            toast.error('Choose at least one invoice type.');
            return;
        }
        if (manualInvoiceForm.consolidate_to_rent && !manualInvoiceForm.include_rent) {
            toast.error('Enable Rent when consolidating invoices.');
            return;
        }

        const payload = {
            house_id: parseInt(id, 10),
            user_id: parseInt(manualInvoiceForm.user_id, 10),
            months: [...manualInvoiceForm.months],
            year: parseInt(manualInvoiceForm.year, 10),
            invoice_types: [
                ...(manualInvoiceForm.include_rent ? [{ reference: 'rent' }] : []),
                ...(manualInvoiceForm.include_deposit ? [{ reference: 'deposit' }] : []),
                ...(manualInvoiceForm.include_booking ? [{ reference: 'booking' }] : []),
                ...manualInvoiceForm.expense_ids.map((expenseId) => ({
                    reference: 'expense',
                    expense_id: expenseId,
                })),
            ],
            consolidate_to_rent: manualInvoiceForm.consolidate_to_rent,
            invoice_period: manualInvoicePeriod,
            generation_date: manualInvoiceForm.generation_date,
        };

        setIsCreatingManualInvoice(true);
        serverUrl.post('/landlord/house-invoices/manual-create/', payload, config)
            .then((res) => {
                const createdCount = res.data?.created_count || 0;
                const skippedCount = res.data?.skipped_count || 0;
                const failedCount = res.data?.failed_count || 0;
                toast.success(`Done. Created ${createdCount}, skipped ${skippedCount}, failed ${failedCount}.`);
                setShowAddInvoiceModal(false);
                fetchHouseInvoices(tenantUserId || manualInvoiceForm.user_id, occupancyId);
            })
            .catch((error) => {
                const errorMessage = error?.response?.data?.error || error?.response?.data?.detail || 'Failed to create invoice(s).';
                toast.error(errorMessage);
            })
            .finally(() => {
                setIsCreatingManualInvoice(false);
            });
    };

    const handleOpenDeleteInvoiceModal = (invoice) => {
        setInvoicePendingDelete(invoice);
        setShowDeleteInvoiceModal(true);
    };

    const handleCloseDeleteInvoiceModal = () => {
        if (deletingInvoiceId) {
            return;
        }
        setShowDeleteInvoiceModal(false);
        setInvoicePendingDelete(null);
    };

    const handleOpenEditInvoiceModal = (invoice) => {
        setInvoicePendingEdit(invoice);
        setEditInvoiceAmount(invoice.amount);
        setEditInvoiceMonth(invoice.month?.toLowerCase() || "");
        setEditInvoiceYear(invoice.year || "");
        setEditInvoiceUser(invoice.user || "");
        setEditInvoicePeriod(invoice.invoice_period || "monthly");
        setShowEditInvoiceModal(true);
    };

    const handleCloseEditInvoiceModal = () => {
        if (isEditingInvoice) return;
        setShowEditInvoiceModal(false);
        setInvoicePendingEdit(null);
        setEditInvoiceAmount('');
        setEditInvoiceMonth('');
        setEditInvoiceYear('');
        setEditInvoiceUser('');
        setEditInvoicePeriod('');
    };

    const handleConfirmEditInvoice = (e) => {
        e.preventDefault();
        if (!invoicePendingEdit) return;

        setIsEditingInvoice(true);
        serverUrl.patch(`/landlord/tenant-invoice/${invoicePendingEdit.id}/`, {
            amount: editInvoiceAmount,
            month: editInvoiceMonth,
            year: editInvoiceYear,
            user: editInvoiceUser,
            invoice_period: editInvoicePeriod
        }, config)
            .then(() => {
                toast.success(`Invoice INV ${invoicePendingEdit.id} successfully updated.`);
                fetchHouseInvoices(tenantUserId || invoicePendingEdit.user, occupancyId);
                setShowEditInvoiceModal(false);
                setInvoicePendingEdit(null);
            })
            .catch((error) => {
                const errorMessage = error?.response?.data?.error || error?.response?.data?.detail || 'Failed to edit invoice.';
                toast.error(errorMessage);
            })
            .finally(() => {
                setIsEditingInvoice(false);
            });
    };

    const handleConfirmDeleteInvoice = () => {
        if (!invoicePendingDelete) {
            return;
        }

        setDeletingInvoiceId(invoicePendingDelete.id);
        serverUrl.delete(`/landlord/tenant-invoice/${invoicePendingDelete.id}/`, config)
            .then(() => {
                toast.success(`Invoice INV ${invoicePendingDelete.id} deleted and reconciliation reversed.`);
                fetchHouseInvoices(tenantUserId || invoicePendingDelete.user, occupancyId);
                setShowDeleteInvoiceModal(false);
                setInvoicePendingDelete(null);
            })
            .catch((error) => {
                const errorMessage = error?.response?.data?.error || error?.response?.data?.detail || 'Failed to delete invoice.';
                toast.error(errorMessage);
            })
            .finally(() => {
                setDeletingInvoiceId(null);
            });
    };

    function getMonthNumber(month) {
        const months = {
            january: 1,
            february: 2,
            march: 3,
            april: 4,
            may: 5,
            june: 6,
            july: 7,
            august: 8,
            september: 9,
            october: 10,
            november: 11,
            december: 12
        };

        return months[month];
    }

    // searching function
    const [sortType, setSortType] = useState('updated_at');
    const [sortOrder, setSortOrder] = useState('desc');
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedRow, setSelectedRow] = useState(null);

    const handleSort = (sortField) => {
        setSortType(sortField);
        setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    };

    const handleSearch = (event) => {
        setSearchQuery(event.target.value);
        setSelectedRow(null);
    };

    const sortedData = useMemo(() => {
        if (!sortType) {
            return [...houseSelectedReport];
        }
        return [...houseSelectedReport].sort((a, b) => {
            const sortValueA = a[sortType];
            const sortValueB = b[sortType];

            if (sortValueA === undefined || sortValueA === null) {
                return sortValueB === undefined || sortValueB === null ? 0 : 1;
            }
            if (sortValueB === undefined || sortValueB === null) {
                return -1;
            }

            const normalizedA = typeof sortValueA === 'string' ? sortValueA.toLowerCase() : sortValueA;
            const normalizedB = typeof sortValueB === 'string' ? sortValueB.toLowerCase() : sortValueB;

            if (normalizedA === normalizedB) {
                return 0;
            }

            if (sortOrder === 'asc') {
                return normalizedA > normalizedB ? 1 : -1;
            }
            return normalizedA < normalizedB ? 1 : -1;
        });
    }, [houseSelectedReport, sortType, sortOrder]);

    const filteredData = sortedData.filter((item) => {
        // Filter by selected rent
        if (selectedTenant && item.user !== selectedTenant) {
            return false;
        }
        const searchableFields = Object.values(item).join(" ").toLowerCase();
        return searchableFields.includes(searchQuery.toLowerCase());
    });

    // const displayedItems = filteredData

    // design the table to display 10 each per table
    const [currentPage, setCurrentPage] = useState(0);
    const itemsPerPage = 5;

    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    useEffect(() => {
        setCurrentPage(0);
    }, [searchQuery, selectedTenant, sortType, sortOrder, houseSelectedReport.length, tenantUserId, occupancyId]);

    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages - 1) {
            setCurrentPage(totalPages - 1);
        }
        if (totalPages === 0 && currentPage !== 0) {
            setCurrentPage(0);
        }
    }, [totalPages, currentPage]);
    const startIndex = currentPage * itemsPerPage;
    const endIndex = currentPage === totalPages - 1 ? filteredData.length : startIndex + itemsPerPage;
    const displayedItems = filteredData.slice(startIndex, endIndex);

    // const displayedItems = filteredData

    const handlePrevPage = () => {
        setCurrentPage(currentPage - 1);
    };

    const handleNextPage = () => {
        setCurrentPage(currentPage + 1);
    };

    // add payments
    // handle when the house is selected
    const [houseSelectedId, setHouseSelectedId] = useState("");
    const [invoices, setInvoices] = useState([])
    const [invoiceSelectedId, setInvoiceSelectedId] = useState("");
    const [houseSelectedName, setHouseSelectedName] = useState("")
    const [apartmentHouse, setApartmentHouse] = useState([]);
    const [selectedApartmentId, setSelectedApartmentId] = useState("");
    const [selectedUtilities, setSelectedUtilities] = useState([])
    const [selectedHouseId, setSelectedHouseId] = useState(null);

    // Helper function to format phone number
    function formatPhoneNumber(phone) {
        if (!phone) return '254';
        const cleaned = phone.toString().replace(/\D/g, '');
        if (cleaned.startsWith('0')) return '254' + cleaned.substring(1);
        if (cleaned.startsWith('254')) return cleaned;
        return '254' + cleaned;
    }

    useEffect(() => {

        if (tenantDetails?.phone_number) {
            const formatted = formatPhoneNumber(tenantDetails.phone_number);
            setFormDataPayment(prev => ({
                ...prev,
                phone_number: formatted
            }));
        }
    }, [tenantDetails]);




    // form data for tenant details ratings
    const [formDataPayment, setFormDataPayment] = useState({
        apartment: "",
        house_number: "",
        category: '',
        utility_type: '',
        source: '',
        amount: '',
        paid_date: '',
        phone_number: formatPhoneNumber(tenantDetails?.phone_number) || '254'
    });
    // onchange functionality on rating tenant
    const handleChangePayment = (event) => {
        const { name, value } = event.target;
        let errorMessage = "";

        if (name === "phone_number") {
            // Check if the entered number starts with '+254' and has at most 12 digits
            if (!value.startsWith("254") || value.length > 13 || isNaN(value.slice(4))) {
                errorMessage = "Invalid phone number format. Please enter a valid number starting with '254' and followed by up to 9 digits.";
            }
        }

        setFormDataPayment({
            ...formDataPayment,
            [name]: value,
            errorMessage: errorMessage
        });

    }



    // handle when the utility is selected
    const [selectedCategory, setSelectedCategory] = useState("");
    const [selectedUtility, setSelectedUtility] = useState("");

    const handleCategorySelect = (e) => {
        setSelectedCategory(e.target.value);
        setSelectedUtility("");
    };

    // handle when the type of payment is selected
    const [selectedMethod, setSelectedMethod] = useState("");
    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState("");

    const handlePaymentSelect = (e) => {
        setSelectedMethod(e.target.value);
        setSelectedPaymentMethod("");
    };


    // the invoice selected
    function handleInvoiceSelect(selectedInvoiceid) {
        setInvoiceSelectedId(selectedInvoiceid)

    }

    // save the data to the backend on the payment
    function handleSavePayment(e) {
        setIsSaving(true)

        // prevent form from redirecting to another page
        e.preventDefault();

        if (paymentTreatment === 'deposit' || paymentTreatment === 'withholding_tax') {
            const settlementPayload = {
                invoice_id: selectedPaymentInvoice.id,
                amount: formDataPayment.amount,
                phone_number: formDataPayment.phone_number,
                paid_date: formDataPayment.paid_date,
                source: paymentTreatment === 'deposit' ? 'deposit' : selectedMethod,
                payment_treatment: paymentTreatment,
                ...(paymentTreatment === 'withholding_tax' && withholdingMode === 'custom'
                    ? { withholding_tax_amount: withholdingTaxAmount }
                    : {}),
            };
            serverUrl.post('/landlord/manual-invoice-settlement/', settlementPayload, config)
                .then((res) => {
                    setShowModalPayment(false);
                    setSelectedPaymentInvoice({});
                    setSelectedMethod('');
                    setPaymentTreatment('cash');
                    setFormDataPayment((previous) => ({
                        ...previous,
                        amount: '',
                        paid_date: '',
                    }));
                    fetchHouseInvoices(tenantUserId, occupancyId);
                    toast.success(res?.data?.message || 'Invoice settlement recorded successfully!');
                })
                .catch((error) => {
                    toast.error(
                        error?.response?.data?.message
                        || error?.response?.data?.error
                        || 'Failed to record invoice settlement'
                    );
                })
                .finally(() => setIsSaving(false));
            return;
        }
        const getCurrentTime = () => {
            const now = new Date();
            const year = now.getFullYear();
            const month = String(now.getMonth() + 1).padStart(2, '0');
            const day = String(now.getDate()).padStart(2, '0');
            const hours = String(now.getHours()).padStart(2, '0');
            const minutes = String(now.getMinutes()).padStart(2, '0');
            const seconds = String(now.getSeconds()).padStart(2, '0');

            return `${year}${month}${day}${hours}${minutes}${seconds}`;
        };
        // Add current time (hours, minutes, seconds)
        const currentTime = getCurrentTime();
        // get the details of the tenant
        const firstName = tenantDetails.full_name.split(' ')[0];
        const originalDate = formDataPayment.paid_date; // "2024-05-20"
        const dateObj = new Date(originalDate);

        // Get date components
        const year = dateObj.getFullYear();
        const month = String(dateObj.getMonth() + 1).padStart(2, "0");
        const day = String(dateObj.getDate()).padStart(2, "0");

        // Get current time components
        const now = new Date();
        const hours = String(now.getHours()).padStart(2, "0");
        const minutes = String(now.getMinutes()).padStart(2, "0");
        const seconds = String(now.getSeconds()).padStart(2, "0");

        // Combine into final format
        const formattedDate = `${year}${month}${day}${hours}${minutes}${seconds}`;

        const formData = {
            transcation_type: selectedMethod,
            transcation_id: selectedMethod + unitDetails.house_number,
            bill_ref_number: unitDetails.house_number,
            apartment: unitDetails.apartment,
            transcation_time: formattedDate,
            transcation_amount: formDataPayment.amount,
            phone_number: formDataPayment.phone_number,
            first_name: firstName,
        };

        // post the data to the ctob
        serverUrl.post(`/landlord/c2btranscation/`, formData, config)
            .then((res) => {
                const reconcileParams = new URLSearchParams({
                    c2btransaction_id: String(res.data.id),
                    house_id: String(unitDetails.id),
                    payment_category: selectedPaymentInvoice.reference || "",
                    expense_id: selectedPaymentInvoice.description || "",
                    invoice_month: selectedPaymentInvoice.month || "",
                    invoice_year: String(selectedPaymentInvoice.year || ""),
                });

                if (selectedPaymentInvoice?.id) {
                    reconcileParams.append("invoice_id", String(selectedPaymentInvoice.id));
                }

                serverUrl.get(`/landlord/reconcile_transactions/?${reconcileParams.toString()}`, config)
                    .then(() => {
                        setShowModalPayment(false)
                        setIsSaving(false)
                        fetchHouseInvoices(tenantUserId, occupancyId);
                        setSelectedPaymentInvoice({})
                        setSelectedMethod("")
                        setFormDataPayment({
                            apartment: "",
                            house_number: "",
                            category: '',
                            utility_type: '',
                            source: '',
                            amount: '',
                            paid_date: '',
                            phone_number: formatPhoneNumber(tenantDetails?.phone_number) || '254'
                        })

                        toast.success('Successfully added the payment!')

                    })
                    .catch((error) => {
                        console.log(error)
                        setIsSaving(false)
                        toast.error(error?.response?.data?.message || error?.response?.data?.error || 'Failed to reconcile payment')

                    })

            })
            .catch((error) => {
                setIsSaving(false)
                console.log(error)
                toast.error(error?.response?.data?.message || error?.response?.data?.error || 'Failed to add payment')
            })

    }


    const exportCSV = () => {
        const csvData = [
            ['Amount', 'Status', 'Payment Category', 'Month', 'Year', 'Date'],
            ...houseSelectedReport.map((item) => [item.amount, item.status, item.reference, item.month, item.year, new Date(item.updated_at).toISOString().split('T')[0]]),
        ];
        const csvBlob = new Blob([csvData.join('\n')], { type: 'text/csv;charset=utf-8;' });
        saveAs(csvBlob, 'data.csv');
    };

    if (isLoading) {
        return (
            <div className="loader-container">
                <div className="loader"></div>
            </div>
        );
    }
    function getNextMonth(currentMonth) {
        const months = ['january', 'february', 'march', 'april', 'may', 'june',
            'july', 'august', 'september', 'october', 'november', 'december'];
        const currentIndex = months.indexOf(currentMonth.toLowerCase());
        return months[(currentIndex + 1) % 12];
    }
    function capitalizeFirstLetter(string) {
        return string.charAt(0).toUpperCase() + string.slice(1);
    }
    return (
        <>
            {selectedUnit.id === idHouse ? (
                <div className="HistoryReport" >
                    {/* list of the tenants of the apartment and payment details */}
                    <div className="tasks ">
                        <h2>Invoice report - {selectedUnit.house_number} </h2>
                        <p>
                            Current Status :
                            {selectedUnit.occupancy_status === 'occupied' ? (
                                <b className="text-success text-uppercase"> {selectedUnit.occupancy_status}</b>
                            ) : (
                                <b className="text-danger text-uppercase">{selectedUnit.occupancy_status}</b>
                            )}
                        </p>
                        <div className="d-flex justify-content-between">
                            <div>
                                <Button className="btn btn-success text-white me-3" onClick={exportCSV}>
                                    {' '}
                                    EXCEL <i className="bi bi-file-earmark-excel "></i>
                                </Button>
                                <Button className="btn btn-danger text-white" onClick={handleDownload}>
                                    PDF <i className="bi bi-file-pdf-fill"></i>
                                </Button>
                                <Button className="btn btn-dark text-white ms-3" onClick={handleViewRentalStatement}>
                                    Statement <i className="bi bi-file-earmark-text-fill"></i>
                                </Button>
                                <Button className="btn btn-primary text-white ms-3" onClick={handleOpenAddInvoiceModal}>
                                    Add Invoice <i className="bi bi-plus-circle-fill"></i>
                                </Button>

                            </div>
                            <Form className="searchBar">
                                <FormControl type="text" placeholder="Search" onChange={handleSearch} />
                            </Form>
                        </div>

                        <Form.Select size='sm' className='formProperty d-none' value={selectedTenant} onChange={(e) => setTenantSelected((e.target.value))}>
                            <option disabled selected>Filter By:</option>
                            <option value="">All Tenants</option>
                            {houseSelectedReport.map((users) => (
                                <option value={users.user}>{tenantsInvoice[users.user]}</option>
                            ))}
                        </Form.Select>

                        <div className="overflow-auto max-h-full mt-3 rounded-lg border border-gray-200" style={{ minWidth: '10px' }}>
                            <table className="min-w-full divide-y divide-gray-200 mt-3 overflow-y-scroll w-50">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" onClick={() => handleSort('id')}>
                                            Invoice Id
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" onClick={() => handleSort('amount')}>
                                            Amount
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" onClick={() => handleSort('status')}>
                                            Status
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" onClick={() => handleSort('reference')}>
                                            Reference
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" onClick={() => handleSort('month')}>
                                            Month
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" onClick={() => handleSort('year')}>
                                            Year
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" onClick={() => handleSort('updated_at')}>
                                            Date
                                        </th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">View</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Download</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Payment</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Edit</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Delete</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {displayedItems
                                        .sort((a, b) => {
                                            // Sort by year first in descending order
                                            if (b.year !== a.year) {
                                                return b.year - a.year;
                                            }
                                            // If years are the same, sort by month in descending order
                                            return getMonthNumber(b.month) - getMonthNumber(a.month);
                                        })
                                        .map((houseHistory) => (
                                            <tr key={houseHistory.id}>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">INV {houseHistory.id}</td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    KES. {Number(houseHistory.effective_amount ?? houseHistory.amount ?? 0).toLocaleString('en-us')}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">{houseHistory.status}</td>
                                                {houseHistory.reference === "expense" ?
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">utility</td>
                                                    :
                                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">{houseHistory.reference}</td>
                                                }
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {(() => {
                                                        const period = normalizeInvoicePeriod(houseHistory.invoice_period);
                                                        if (period === 'quarterly') {
                                                            return `${capitalizeFirstLetter(houseHistory.month)}, ${capitalizeFirstLetter(getNextMonth(houseHistory.month))}, ${capitalizeFirstLetter(houseHistory.end_month)}`;
                                                        }
                                                        if (period === 'semi-annual' || period === 'annually') {
                                                            return `${capitalizeFirstLetter(houseHistory.month)} - ${capitalizeFirstLetter(houseHistory.end_month)}`;
                                                        }
                                                        return capitalizeFirstLetter(houseHistory.month);
                                                    })()}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {houseHistory.end_year
                                                        ? (houseHistory.year !== houseHistory.end_year
                                                            ? `${houseHistory.year} - ${houseHistory.end_year}`
                                                            : houseHistory.year)
                                                        : houseHistory.year}
                                                </td>
                                                <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    {houseHistory.generation_date || new Date(houseHistory.created_at).toISOString().split('T')[0]}
                                                </td>

                                                <td>
                                                    <Button
                                                        className="btn btn-primary text-white btn-sm"
                                                        onClick={() => handleViewPDFInvoice(houseHistory.id)}
                                                    >
                                                        View <i className="bi bi-eye-fill"></i>
                                                    </Button>
                                                </td>
                                                <td>
                                                    <Button
                                                        className="btn btn-danger text-white btn-sm me-3"
                                                        onClick={() => handleDownloadInvoice(houseHistory.id)}
                                                    >
                                                        PDF <i className="bi bi-file-pdf-fill"></i>
                                                    </Button>
                                                </td>
                                                <td>
                                                    {houseHistory.status !== "paid" ? (
                                                        <Button
                                                            className="btn w-100 btn-warning text-white btn-sm"
                                                            onClick={(e) => handleOpenModalPayment(houseHistory)}>
                                                            Add Payment
                                                        </Button>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td>
                                                    {houseHistory.status !== "paid" ? (
                                                        <Button
                                                            className="btn btn-sm btn-info text-white me-3"
                                                            onClick={() => handleOpenEditInvoiceModal(houseHistory)}
                                                            title="Edit invoice"
                                                        >
                                                            Edit <i className="bi bi-pencil"></i>
                                                        </Button>
                                                    ) : (
                                                        <span className="text-muted">-</span>
                                                    )}
                                                </td>
                                                <td>
                                                    <Button
                                                        className={`btn btn-sm d-inline-flex align-items-center gap-1 ${deletingInvoiceId === houseHistory.id ? 'btn-secondary' : 'btn-danger text-white'}`}
                                                        disabled={deletingInvoiceId === houseHistory.id}
                                                        onClick={() => handleOpenDeleteInvoiceModal(houseHistory)}
                                                        title="Delete invoice"
                                                    >
                                                        {deletingInvoiceId === houseHistory.id ? (
                                                            <>
                                                                <Loader2 size={14} className="animate-spin" />
                                                                Deleting
                                                            </>
                                                        ) : (
                                                            <>
                                                                <Trash2 size={14} />
                                                                Delete
                                                            </>
                                                        )}
                                                    </Button>
                                                </td>
                                            </tr>
                                        ))}

                                </tbody>

                            </table>
                        </div>



                        <Modal show={showEditInvoiceModal} onHide={handleCloseEditInvoiceModal} centered size="lg">
                            <Modal.Header closeButton className="bg-light">
                                <Modal.Title>Edit Invoice Details</Modal.Title>
                            </Modal.Header>
                            <Form onSubmit={handleConfirmEditInvoice}>
                                <Modal.Body className="p-4">
                                    <p className="text-muted mb-4">
                                        Update the details for 
                                        <strong> {invoicePendingEdit ? `INV ${invoicePendingEdit.id}` : 'this invoice'}</strong>.
                                    </p>
                                    
                                    <div className="row g-3">
                                        <Col md={12}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-medium">Assigned Tenant</Form.Label>
                                                <Form.Select 
                                                    value={editInvoiceUser} 
                                                    onChange={(e) => setEditInvoiceUser(e.target.value)} 
                                                    required
                                                >
                                                    <option value="" disabled>--- Select Tenant ---</option>
                                                    {tenantOptions.map((opt) => (
                                                        <option key={opt.user_id} value={opt.user_id}>
                                                            {opt.tenant_name} {opt.is_current ? '(Current)' : '(Past Tenant)'}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-medium">Invoicing Frequency</Form.Label>
                                                <Form.Select 
                                                    value={editInvoicePeriod} 
                                                    onChange={(e) => setEditInvoicePeriod(e.target.value)}
                                                    required
                                                >
                                                    <option value="monthly">Monthly</option>
                                                    <option value="quarterly">Quarterly</option>
                                                    <option value="semi-annual">Semi-Annually</option>
                                                    <option value="annually">Annually</option>
                                                    <option value="once">Once</option>
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-medium">Invoice Month</Form.Label>
                                                <Form.Select 
                                                    value={editInvoiceMonth} 
                                                    onChange={(e) => setEditInvoiceMonth(e.target.value)}
                                                    required
                                                    className="text-capitalize"
                                                >
                                                    <option value="" disabled>Select Month</option>
                                                    {monthNames.map((month) => (
                                                        <option key={month} value={month}>
                                                            {capitalizeFirstLetter(month)}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-medium">Invoice Year</Form.Label>
                                                <Form.Control 
                                                    type="number" 
                                                    value={editInvoiceYear} 
                                                    onChange={(e) => setEditInvoiceYear(e.target.value)}
                                                    min="2000"
                                                    max="2100"
                                                    required 
                                                />
                                            </Form.Group>
                                        </Col>

                                        <Col md={6}>
                                            <Form.Group className="mb-3">
                                                <Form.Label className="fw-medium">Invoice Amount (KES)</Form.Label>
                                                <Form.Control 
                                                    type="number" 
                                                    value={editInvoiceAmount} 
                                                    onChange={(e) => setEditInvoiceAmount(e.target.value)} 
                                                    required 
                                                />
                                            </Form.Group>
                                        </Col>
                                    </div>
                                </Modal.Body>
                                <Modal.Footer className="bg-light">
                                    <Button variant="secondary" onClick={handleCloseEditInvoiceModal} disabled={isEditingInvoice}>
                                        Cancel
                                    </Button>
                                    <Button
                                        variant="primary"
                                        type="submit"
                                        disabled={isEditingInvoice}
                                        className="d-inline-flex align-items-center gap-2 px-4"
                                    >
                                        {isEditingInvoice ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                Saving...
                                            </>
                                        ) : (
                                            <>
                                                Save Changes
                                            </>
                                        )}
                                    </Button>
                                </Modal.Footer>
                            </Form>
                        </Modal>

                        <Modal show={showDeleteInvoiceModal} onHide={handleCloseDeleteInvoiceModal} centered>
                            <Modal.Header closeButton>
                                <Modal.Title>Confirm Invoice Delete</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <p className="mb-2">
                                    You are about to delete
                                    <strong> {invoicePendingDelete ? `INV ${invoicePendingDelete.id}` : 'this invoice'}</strong>.
                                </p>
                                <p className="mb-2 text-danger">
                                    This will also delete linked transaction records for this invoice.
                                </p>
                                <small className="text-muted">
                                    If the payment came from CTOB, the system will automatically mark it unreconciled and return the amount to overflow.
                                </small>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button variant="secondary" onClick={handleCloseDeleteInvoiceModal} disabled={Boolean(deletingInvoiceId)}>
                                    Cancel
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={handleConfirmDeleteInvoice}
                                    disabled={Boolean(deletingInvoiceId)}
                                    className="d-inline-flex align-items-center gap-2"
                                >
                                    {deletingInvoiceId ? (
                                        <>
                                            <Loader2 size={14} className="animate-spin" />
                                            Deleting...
                                        </>
                                    ) : (
                                        <>
                                            <Trash2 size={14} />
                                            Delete Invoice
                                        </>
                                    )}
                                </Button>
                            </Modal.Footer>
                        </Modal>

                        <Modal show={showAddInvoiceModal} onHide={handleCloseAddInvoiceModal} size="lg" centered>
                            <Modal.Header
                                closeButton
                                style={{
                                    background: 'linear-gradient(120deg, #0f172a 0%, #1d4ed8 55%, #0369a1 100%)',
                                    color: '#fff',
                                    borderBottom: 'none',
                                }}
                            >
                                <Modal.Title className="d-flex align-items-center gap-2">
                                    <DollarSign size={18} />
                                    Add Invoice(s)
                                </Modal.Title>
                            </Modal.Header>
                            <Form onSubmit={handleCreateManualInvoices}>
                                <Modal.Body style={{ background: 'linear-gradient(180deg, #f8fafc 0%, #ffffff 45%, #eef2ff 100%)' }}>
                                    <div className="d-flex flex-wrap align-items-center gap-2 mb-4">
                                        <span className="badge rounded-pill text-bg-primary px-3 py-2">
                                            {manualInvoiceForm.months.length}/3 month(s) selected
                                        </span>
                                        <span className="badge rounded-pill text-bg-dark px-3 py-2">
                                            {[
                                                manualInvoiceForm.include_rent,
                                                manualInvoiceForm.include_deposit,
                                                manualInvoiceForm.include_booking,
                                            ].filter(Boolean).length + manualInvoiceForm.expense_ids.length} type(s) selected
                                        </span>
                                        <span className="badge rounded-pill text-bg-info px-3 py-2 text-capitalize">
                                            {manualInvoicePeriod} billing ({manualCycleMonths} month{manualCycleMonths === 1 ? '' : 's'})
                                        </span>
                                    </div>

                                    <div className="row g-3 mb-3">
                                        <div className="col-md-8">
                                            <div className="p-3 rounded-3 border bg-white shadow-sm h-100">
                                                <Form.Label className="fw-semibold mb-2">Tenant</Form.Label>
                                                <Form.Select
                                                    value={manualInvoiceForm.user_id}
                                                    onChange={(event) => setManualInvoiceForm((prev) => ({ ...prev, user_id: event.target.value }))}
                                                    required
                                                >
                                                    <option value="">Select tenant</option>
                                                    {tenantOptions.map((tenant) => (
                                                        <option key={tenant.user_id} value={tenant.user_id}>
                                                            {tenant.tenant_name} {tenant.is_current ? '(Current)' : '(Previous)'}
                                                        </option>
                                                    ))}
                                                </Form.Select>
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="p-3 rounded-3 border bg-white shadow-sm h-100">
                                                <Form.Label className="fw-semibold mb-2">Year</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    min="2000"
                                                    max="2100"
                                                    value={manualInvoiceForm.year}
                                                    onChange={(event) => setManualInvoiceForm((prev) => ({ ...prev, year: event.target.value }))}
                                                    required
                                                />
                                            </div>
                                        </div>
                                        <div className="col-md-4">
                                            <div className="p-3 rounded-3 border bg-white shadow-sm h-100">
                                                <Form.Label className="fw-semibold mb-2">Invoice Generation Date</Form.Label>
                                                <Form.Control
                                                    type="date"
                                                    max={todayValue}
                                                    value={manualInvoiceForm.generation_date}
                                                    onChange={(event) => setManualInvoiceForm((prev) => ({ ...prev, generation_date: event.target.value }))}
                                                    required
                                                />
                                                <small className="text-muted">Choose an earlier date to backdate the invoice.</small>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-3 rounded-3 border bg-white shadow-sm mb-3">
                                        <Form.Label className="fw-semibold mb-2">
                                            {manualInvoiceForm.include_rent && manualCycleMonths > 1 ? 'Billing-cycle starting month' : 'Months (up to 3)'}
                                        </Form.Label>
                                        <div className="d-flex flex-wrap gap-2">
                                            {monthNames.map((month) => {
                                                const isChecked = manualInvoiceForm.months.includes(month);
                                                const cycleRent = manualInvoiceForm.include_rent && manualCycleMonths > 1;
                                                const disableMonth = !cycleRent && !isChecked && manualInvoiceForm.months.length >= 3;
                                                return (
                                                    <button
                                                        key={month}
                                                        type="button"
                                                        className={`btn btn-sm rounded-pill px-3 ${isChecked ? 'btn-primary' : 'btn-outline-secondary'}`}
                                                        disabled={disableMonth}
                                                        onClick={() => handleManualMonthToggle(month)}
                                                    >
                                                        {isChecked ? '[x] ' : ''}{capitalizeFirstLetter(month)}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        {manualInvoiceForm.include_rent && manualCycleMonths > 1 && (
                                            <small className="text-muted d-block mt-2">
                                                The amount and ending month are calculated automatically from this unit's {manualInvoicePeriod} billing cycle.
                                            </small>
                                        )}
                                    </div>

                                    <div className="p-3 rounded-3 border bg-white shadow-sm mb-3">
                                        <Form.Label className="fw-semibold mb-2">Core Invoice Types</Form.Label>
                                        <div className="row g-2">
                                            <div className="col-md-4">
                                                <button
                                                    type="button"
                                                    className={`btn w-100 text-start rounded-3 ${manualInvoiceForm.include_rent ? 'btn-primary' : 'btn-outline-primary'}`}
                                                    onClick={() => setManualInvoiceForm((prev) => ({
                                                        ...prev,
                                                        include_rent: !prev.include_rent,
                                                        months: !prev.include_rent && manualCycleMonths > 1 ? prev.months.slice(0, 1) : prev.months,
                                                    }))}
                                                >
                                                    Rent {manualInvoiceForm.include_rent ? '[x]' : ''}
                                                </button>
                                            </div>
                                            <div className="col-md-4">
                                                <button
                                                    type="button"
                                                    className={`btn w-100 text-start rounded-3 ${manualInvoiceForm.include_deposit ? 'btn-success' : 'btn-outline-success'}`}
                                                    onClick={() => setManualInvoiceForm((prev) => ({ ...prev, include_deposit: !prev.include_deposit }))}
                                                >
                                                    Deposit {manualInvoiceForm.include_deposit ? '[x]' : ''}
                                                </button>
                                            </div>
                                            <div className="col-md-4">
                                                <button
                                                    type="button"
                                                    className={`btn w-100 text-start rounded-3 ${manualInvoiceForm.include_booking ? 'btn-warning text-white' : 'btn-outline-warning'}`}
                                                    onClick={() => setManualInvoiceForm((prev) => ({ ...prev, include_booking: !prev.include_booking }))}
                                                >
                                                    Booking {manualInvoiceForm.include_booking ? '[x]' : ''}
                                                </button>
                                            </div>
                                        </div>
                                        <hr className="my-3" />
                                        <Form.Label className="fw-semibold mb-2">Utilities / Expenses</Form.Label>
                                        {availableExpenses.length === 0 ? (
                                            <small className="text-muted">No visible expenses found for this apartment.</small>
                                        ) : (
                                            <div className="d-flex flex-wrap gap-2">
                                                {availableExpenses.map((expense) => {
                                                    const isSelected = manualInvoiceForm.expense_ids.includes(expense.id);
                                                    return (
                                                        <button
                                                            key={expense.id}
                                                            type="button"
                                                            className={`btn btn-sm rounded-pill px-3 ${isSelected ? 'btn-dark' : 'btn-outline-dark'}`}
                                                            onClick={() => handleManualExpenseToggle(expense.id)}
                                                        >
                                                            {isSelected ? '[x] ' : ''}{expense.title}{expense.expense_type ? ` (${expense.expense_type})` : ''}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </div>

                                    {(!manualInvoiceForm.include_rent || manualCycleMonths === 1) && <div className="p-3 rounded-3 border bg-white shadow-sm mb-1">
                                        <Form.Check
                                            type="switch"
                                            id="consolidate-rent"
                                            label="Consolidate selected months/types into one rent invoice"
                                            checked={manualInvoiceForm.consolidate_to_rent}
                                            onChange={(event) => setManualInvoiceForm((prev) => ({ ...prev, consolidate_to_rent: event.target.checked }))}
                                        />
                                        <small className="text-muted d-block mt-1">
                                            Useful when you want one combined rent invoice instead of separate records.
                                        </small>
                                    </div>}
                                </Modal.Body>
                                <Modal.Footer style={{ background: '#f8fafc' }}>
                                    <Button variant="secondary" onClick={handleCloseAddInvoiceModal} disabled={isCreatingManualInvoice}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" variant="primary" disabled={isCreatingManualInvoice} className="px-4 d-inline-flex align-items-center gap-2">
                                        {isCreatingManualInvoice ? (
                                            <>
                                                <Loader2 size={15} className="animate-spin" />
                                                Creating...
                                            </>
                                        ) : (
                                            <>Create Invoice(s)</>
                                        )}
                                    </Button>
                                </Modal.Footer>
                            </Form>
                        </Modal>

                        <Modal show={showModalPayment} onHide={handleCloseModalPayment}>
                            <div className="relative  p-0 rounded-xl overflow-hidden shadow-2xl bg-gradient-to-br from-white to-gray-50">
                                {/* Header */}
                                <div className="relative bg-gradient-to-r from-blue-600 to-indigo-700 p-6 text-white">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center space-x-3">
                                            <Wallet className="w-6 h-6" />
                                            <h3 className="text-xl font-bold">Add Payment</h3>
                                        </div>
                                        <button
                                            onClick={handleCloseModalPayment}
                                            className="p-1 rounded-full hover:bg-white/20 transition-colors"
                                        >
                                            <X className="w-5 h-5" />
                                        </button>
                                    </div>
                                    <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20"></div>
                                </div>

                                {/* Body */}
                                <div className="p-6 space-y-4">
                                    <form onSubmit={handleSavePayment} className="space-y-5">
                                        {/* Payment Method */}
                                        <div className="space-y-1">
                                            <p className="flex items-center text-sm font-medium text-gray-700">
                                                <Landmark className="w-4 h-4 mr-2 text-indigo-600" />
                                                Payment Method
                                            </p>
                                            <div className="relative">
                                                <select
                                                    className="w-full px-4 py-2 pl-10 pr-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-700 appearance-none"
                                                    value={paymentTreatment === 'deposit' ? 'deposit' : selectedMethod}
                                                    onChange={handlePaymentSelect}
                                                    disabled={paymentTreatment === 'deposit'}
                                                    required={paymentTreatment !== 'deposit'}
                                                >
                                                    <option value="" disabled>Select Payment Method</option>
                                                    {paymentTreatment === 'deposit' && <option value="deposit">Tenant Deposit</option>}
                                                    <option value="cash">Cash</option>
                                                    <option value="bank">Bank Payment</option>
                                                    <option value="direct-to-phone">Direct To Phone</option>
                                                </select>

                                            </div>
                                        </div>

                                        <div className="space-y-3 rounded-lg border border-gray-200 bg-gray-50 p-4">
                                            <p className="text-sm font-semibold text-gray-800">Payment treatment</p>
                                            <label className="flex cursor-pointer items-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    className="mt-1"
                                                    checked={paymentTreatment === 'deposit'}
                                                    onChange={(event) => handlePaymentTreatmentChange('deposit', event.target.checked)}
                                                />
                                                <span>
                                                    <span className="block text-sm font-medium text-gray-800">Apply tenant deposit</span>
                                                    <span className="block text-xs text-gray-500">
                                                        Settles the invoice from the tenant's available deposit and does not count as income for this month.
                                                    </span>
                                                </span>
                                            </label>
                                            <label className="flex cursor-pointer items-start gap-3">
                                                <input
                                                    type="checkbox"
                                                    className="mt-1"
                                                    checked={paymentTreatment === 'withholding_tax'}
                                                    disabled={selectedPaymentInvoice?.reference !== 'rent'}
                                                    onChange={(event) => handlePaymentTreatmentChange('withholding_tax', event.target.checked)}
                                                />
                                                <span>
                                                    <span className="block text-sm font-medium text-gray-800">16% withholding tax deducted</span>
                                                    <span className="block text-xs text-gray-500">
                                                        The tenant paid the invoice balance less 16% of the original rent. The withheld portion settles the balance but is not recorded as cash received.
                                                    </span>
                                                </span>
                                            </label>
                                            {paymentTreatment === 'withholding_tax' && (
                                                <div className="ml-7 space-y-2">
                                                    <label className="flex items-center gap-2 text-sm">
                                                        <input type="radio" name="withholding_mode" checked={withholdingMode === 'preset'}
                                                            onChange={() => {
                                                                setWithholdingMode('preset');
                                                                const invoiceAmount = Number(selectedPaymentInvoice?.effective_balance ?? selectedPaymentInvoice?.effective_amount ?? selectedPaymentInvoice?.amount ?? 0);
                                                                const rentAmount = Number(selectedPaymentInvoice?.billing_breakdown?.rent || unitDetails?.rent || 0);
                                                                const tax = rentAmount * 0.16;
                                                                setWithholdingTaxAmount(tax.toFixed(2));
                                                                setFormDataPayment(previous => ({ ...previous, amount: Math.max(invoiceAmount - tax, 0).toFixed(2) }));
                                                            }} />
                                                        Use precalculated 16%
                                                    </label>
                                                    <label className="flex items-center gap-2 text-sm">
                                                        <input type="radio" name="withholding_mode" checked={withholdingMode === 'custom'}
                                                            onChange={() => { setWithholdingMode('custom'); setWithholdingTaxAmount(''); }} />
                                                        Enter a specific withholding amount
                                                    </label>
                                                    {withholdingMode === 'custom' && (
                                                        <input type="number" min="0" step="0.01" value={withholdingTaxAmount}
                                                            onChange={(event) => {
                                                                const value = event.target.value;
                                                                const invoiceAmount = Number(selectedPaymentInvoice?.effective_balance ?? selectedPaymentInvoice?.effective_amount ?? selectedPaymentInvoice?.amount ?? 0);
                                                                setWithholdingTaxAmount(value);
                                                                setFormDataPayment(previous => ({ ...previous, amount: Math.max(invoiceAmount - Number(value || 0), 0).toFixed(2) }));
                                                            }} placeholder="Withholding tax amount" required
                                                            className="w-full px-4 py-2 rounded-lg border border-gray-300" />
                                                    )}
                                                </div>
                                            )}
                                        </div>

                                        {/* Category */}
                                        {/* <div className="space-y-1">
                                                <p className="flex items-center text-sm font-medium text-gray-700">
                                                    <Home className="w-4 h-4 mr-2 text-indigo-600" />
                                                    Category
                                                </p>
                                                <div className="relative">
                                                    <select
                                                        className="w-full px-4 py-2 pl-10 pr-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-700 appearance-none"
                                                        value={selectedCategory}
                                                        onChange={handleCategorySelect}
                                                    >
                                                        <option value="" disabled>Select Category</option>
                                                        <option value="expense">Utility</option>
                                                        <option value="rent">Rent</option>
                                                    </select>

                                                </div>
                                            </div> */}

                                        {/* Utility (conditional) */}
                                        {/* {selectedCategory === "expense" && (
                                                <div className="space-y-1">
                                                    <p className="flex items-center text-sm font-medium text-gray-700">
                                                        <Zap className="w-4 h-4 mr-2 text-indigo-600" />
                                                        Utility
                                                    </p>
                                                    <div className="relative">
                                                        <select
                                                            className="w-full px-4 py-2 pl-10 pr-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-700 appearance-none"
                                                            value={selectedUtility}
                                                            onChange={(e) => setSelectedUtility(e.target.value)}
                                                        >
                                                            <option value="" disabled selected>Select Utility</option>
                                                            {selectedUtilities.map((utility) => (
                                                                <option key={utility.id} value={utility.id}>
                                                                    {utility.title}
                                                                </option>
                                                            ))}
                                                        </select>

                                                    </div>
                                                </div>
                                            )} */}

                                        {/* Invoice */}
                                        {/* <div className="space-y-1">
                                                <p className="flex items-center text-sm font-medium text-gray-700">
                                                    <Calendar className="w-4 h-4 mr-2 text-indigo-600" />
                                                    Month Invoice Payment
                                                </p>
                                                <div className="relative">
                                                    <select
                                                        className="w-full px-4 py-2 pl-10 pr-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 bg-white text-gray-700 appearance-none"
                                                        value={invoiceSelectedId}
                                                        onChange={(e) => handleInvoiceSelect(e.target.value)}
                                                    >
                                                        <option value="">Month Invoice Payment</option>
                                                        {monthNames.map((month) => {
                                                            const formattedMonth = month.charAt(0).toUpperCase() + month.slice(1);
                                                            const currentYear = new Date().getFullYear();
                                                            const hasInvoiceForMonth = houseSelectedReport.some(invoice => invoice.month.toLowerCase() === month);

                                                            return (
                                                                <React.Fragment key={month}>
                                                                    {hasInvoiceForMonth ? (
                                                                        houseSelectedReport.map((invoice) => (
                                                                            invoice.month.toLowerCase() === month ? (
                                                                                <option
                                                                                    key={invoice.id}
                                                                                    value={`${invoice.month}-${invoice.year}`}
                                                                                    className={
                                                                                        invoice.status === "paid" ? 'text-green-600 font-medium' :
                                                                                            invoice.status === "partial" ? 'text-yellow-500' :
                                                                                                'text-red-500'
                                                                                    }
                                                                                >
                                                                                    {invoice.month} - {invoice.year} - {invoice.status} - {invoice.amount}
                                                                                </option>
                                                                            ) : null
                                                                        ))
                                                                    ) : (
                                                                        <option key={month} value={`${month}-unknown`}>
                                                                            {formattedMonth} - {currentYear}
                                                                        </option>
                                                                    )}
                                                                </React.Fragment>
                                                            );
                                                        })}
                                                    </select>

                                                </div>
                                            </div> */}

                                        {/* Phone Number */}
                                        <div className="space-y-1">
                                            <p className="flex items-center text-sm font-medium text-gray-700">
                                                <Phone className="w-4 h-4 mr-2 text-indigo-600" />
                                                Phone Number
                                            </p>
                                            <div className="relative">
                                                <input
                                                    type="text"
                                                    name="phone_number"
                                                    placeholder="Enter Phone Number"
                                                    value={formDataPayment.phone_number}
                                                    onChange={handleChangePayment}
                                                    required
                                                    disabled={paymentTreatment === 'deposit'}
                                                    className="w-full px-4 py-2 pl-10 pr-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                />

                                            </div>
                                            {formDataPayment.errorMessage && (
                                                <p className="text-sm text-red-500 mt-1">{formDataPayment.errorMessage}</p>
                                            )}
                                        </div>

                                        {/* Payment Date */}
                                        <div className="space-y-1">
                                            <p className="flex items-center text-sm font-medium text-gray-700">
                                                <Calendar className="w-4 h-4 mr-2 text-indigo-600" />
                                                Payment Date
                                            </p>
                                            <div className="relative">
                                                <input
                                                    type="date"
                                                    name="paid_date"
                                                    value={formDataPayment.paid_date}
                                                    onChange={handleChangePayment}
                                                    required
                                                    className="w-full px-4 py-2 pl-10 pr-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                />

                                            </div>
                                        </div>

                                        {/* Amount */}
                                        <div className="space-y-1">
                                            <p className="flex items-center text-sm font-medium text-gray-700">
                                                <DollarSign className="w-4 h-4 mr-2 text-indigo-600" />
                                                Amount
                                            </p>
                                            <div className="relative">
                                                <input
                                                    type="number"
                                                    min="0.01"
                                                    step="0.01"
                                                    inputMode="decimal"
                                                    name="amount"
                                                    placeholder="Enter Amount"
                                                    value={formDataPayment.amount}
                                                    onChange={handleChangePayment}
                                                    required
                                                    className="w-full px-4 py-2 pl-10 pr-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                                                />

                                            </div>
                                        </div>

                                        {/* Submit Button */}
                                        <div className="pt-4">
                                            <button
                                                type="submit"
                                                disabled={isSaving}
                                                className={`w-full py-2 px-4 text-white font-medium rounded-lg shadow-md transition-all transform hover:scale-[1.01] focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 flex items-center justify-center space-x-2 ${isSaving
                                                    ? 'bg-gray-400 cursor-not-allowed'
                                                    : 'bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800'
                                                    }`}
                                            >
                                                {isSaving ? (
                                                    <>
                                                        <Loader2 className="w-5 h-5 animate-spin" />
                                                        <span>Processing...</span>
                                                    </>
                                                ) : (
                                                    <>
                                                        <Wallet className="w-5 h-5" />
                                                        <span>Save Payment</span>
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                            </div>
                        </Modal>


                        <div className="pagination">
                            <button onClick={handlePrevPage} disabled={currentPage === 0}>
                                &lt;
                            </button>
                            <span>{`${startIndex + 1}-${endIndex} of ${houseSelectedReport.length}`}</span>
                            <button onClick={handleNextPage} disabled={currentPage === totalPages - 1}>
                                &gt;
                            </button>
                        </div>
                    </div>

                </div>
            ) : (
                <></>
            )}
        </>
    )
}

export default InvoiceReport
